import { useEffect, useState } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { handleHardwareBack } from './android/phoneBack';
import { ConnectivityBanner } from './android/ConnectivityBanner';
import { AuthProvider } from './auth/AuthProvider';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ConfirmProvider } from './components/ui/ConfirmDialog';
import { AppRouter } from './routes/AppRouter';
import { PlatformApp } from './platform/PlatformApp';
import { SelfServiceApp } from './selfservice/SelfServiceApp';
import { isSelfServiceHash } from './selfservice/format';
import { RecoveryApp } from './recovery/RecoveryApp';
import { InfoApp, isInfoHash } from './info/InfoApp';
import { isRecoveryHash } from './recovery/format';

// Fase 25B: la consola de PLATAFORMA vive bajo el hash #platform/* y es un shell SEPARADO (auth propia,
// sin AuthProvider tenant). Fase 27A: el signup/onboarding autoservicio vive bajo #signup/#onboarding
// (infraestructura minima, no enlazada desde la nav tenant). El resto de la app es el tenant.
const isPlatformHash = () => window.location.hash.replace(/^#/, '').split('/')[0] === 'platform';
// P-085: `#privacidad` y `#terminos` son páginas PÚBLICAS (sin sesión), para poder enlazarlas desde un correo.
type Shell = 'platform' | 'selfservice' | 'recovery' | 'info' | 'tenant';
const resolveShell = (): Shell => (
  isPlatformHash() ? 'platform'
    : isSelfServiceHash(window.location.hash) ? 'selfservice'
    : isRecoveryHash(window.location.hash) ? 'recovery'
    : isInfoHash(window.location.hash) ? 'info'
    : 'tenant'
);

function App() {
  const [shell, setShell] = useState<Shell>(resolveShell());
  useEffect(() => {
    const onHash = () => setShell(resolveShell());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Android Foundation: botón Back físico. Prioridad — 1) cerrar el modal abierto más reciente
  // (registrado por Modal.tsx), 2) con la sesión abierta, lo que diga el shell «como en una app»
  // (Bloque G): del Inicio sale, de un reporte vuelve a Reportes y de lo demás va al Inicio, 3) fuera
  // del shell, volver en el historial de hash si lo hay, 4) solo entonces dejar que Capacitor cierre la
  // app (nunca en medio de un flujo con navegación/modal pendiente). El orden vive en `phoneBack.ts`.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;

    const listenerPromise = CapacitorApp.addListener('backButton', ({ canGoBack }) => {
      handleHardwareBack(canGoBack, () => { void CapacitorApp.exitApp(); });
    });

    return () => {
      void listenerPromise.then((listener) => listener.remove());
    };
  }, []);

  const shellElement =
    shell === 'platform' ? <PlatformApp />
    : shell === 'selfservice' ? <SelfServiceApp />
    : shell === 'recovery' ? <RecoveryApp />
    : shell === 'info' ? <InfoApp />
    : (
      <AuthProvider>
        <ConfirmProvider><AppRouter /></ConfirmProvider>
      </AuthProvider>
    );

  return (
    <ErrorBoundary>
      <ConnectivityBanner />
      {shellElement}
    </ErrorBoundary>
  );
}

export default App;
